import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/PackageDetails.css";
import startDayIcon from "../assets/start-day-icon.png";
import dayPlanIcon from "../assets/day-plan.png";
import accommodationIcon from "../assets/accomodation.png";
import upload from "../assets/upload.png";
import GoogleMap from "../components/GoogleMaps";

// Constants for better maintainability
const DB_CONFIG = {
  NAME: "PackageDetailsDB",
  VERSION: 1,
  STORE_NAME: "packageData"
};

const API_ENDPOINTS = {
  REVERSE_GEOCODE: '/api/maps/reverse-geocode',
  UPLOAD_IMAGE: '/api/packages/upload-image',
  PACKAGE_DETAILS: '/api/packages'
};

const VALIDATION = {
  MAX_FILE_SIZE: 5 * 1024 * 1024,
  VALID_IMAGE_TYPES: ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"]
};

const PICKUP_MODES = ["available", "not-available"];
const FOODING_OPTIONS = ["veg", "nonveg", "both"];
const MEAL_OPTIONS = ["breakfast", "lunch", "dinner"];
const TIME_OPTIONS = ["AM", "PM"];

// IndexedDB Utilities
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_CONFIG.NAME, DB_CONFIG.VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(DB_CONFIG.STORE_NAME)) {
        db.createObjectStore(DB_CONFIG.STORE_NAME, { keyPath: "id" });
      }
    };
  });
};

const saveToIndexedDB = async (data) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([DB_CONFIG.STORE_NAME], "readwrite");
    const store = transaction.objectStore(DB_CONFIG.STORE_NAME);

    const item = {
      id: "currentPackage",
      data: data,
      timestamp: Date.now(),
      version: "1.0",
    };

    await store.put(item);
    return true;
  } catch (error) {
    console.error("IndexedDB save error:", error);
    throw error;
  }
};

const loadFromIndexedDB = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([DB_CONFIG.STORE_NAME], "readonly");
    const store = transaction.objectStore(DB_CONFIG.STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get("currentPackage");
      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("IndexedDB load error:", error);
    return null;
  }
};

const clearIndexedDB = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([DB_CONFIG.STORE_NAME], "readwrite");
    const store = transaction.objectStore(DB_CONFIG.STORE_NAME);

    await store.delete("currentPackage");
    return true;
  } catch (error) {
    console.error("IndexedDB clear error:", error);
    return false;
  }
};

// Helper function for empty day template
const getEmptyDayTemplate = () => ({
  startLocation: "",
  startTime: "",
  startAmPm: TIME_OPTIONS[0],
  pickupMode: PICKUP_MODES[0],
  pickupDetails: "",
  foodingOption: FOODING_OPTIONS[0],
  selectedMeals: [],
  dayDescription: "",
  activities: [], // FIXED: Start with empty array
  stayType: "",
  propertyName: "",
  propertyAddress: "",
  accommodationImages: [],
  isSaved: false,
  locationCoords: null,
  noAccommodation: false,
  dayNumber: 0,
  nightNumber: null,
  hasOvernightStay: true,
});

// Utility functions (without useCallback - these are pure functions)
const capitalizeWords = (str) => {
  if (!str) return "";
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const normalizeCoordinates = (coords) => {
  if (!coords) return null;
  
  let lat, lng;
  
  if (coords.lat !== undefined && coords.lng !== undefined) {
    lat = coords.lat;
    lng = coords.lng;
  } else if (coords.latitude !== undefined && coords.longitude !== undefined) {
    lat = coords.latitude;
    lng = coords.longitude;
  } else {
    return null;
  }
  
  if (typeof lat !== 'number' || typeof lng !== 'number' || 
      isNaN(lat) || isNaN(lng) ||
      lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  
  return { lat, lng };
};

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Confirmation Modal Component
const ConfirmationModal = React.memo(({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = "Yes",
  cancelText = "No",
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="packagedetails-modal-content">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="modal-actions">
          <button className="cancel-btn" onClick={onCancel}>
            {cancelText}
          </button>
          <button className="confirm-btn" onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
});

// Price Popup Component
const PricePopup = React.memo(({ isOpen, onConfirm, onCancel }) => {
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPrice("");
      setError("");
    }
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    const priceValue = price.trim();

    if (!priceValue || isNaN(priceValue) || parseFloat(priceValue) <= 0) {
      setError("Please enter a valid price amount");
      return;
    }

    const priceData = {
      raw: parseFloat(priceValue),
      formatted: `₹${parseFloat(priceValue).toLocaleString("en-IN")}`,
    };

    onConfirm(priceData);
  }, [price, onConfirm]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === "Enter") {
      handleConfirm();
    }
  }, [handleConfirm]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="packagedetails-modal-content price-modal">
        <h3>Set Package Price</h3>
        <p>Please set the price for your package</p>

        <div className="price-input-container">
          <label>Package Price (₹)</label>
          <input
            type="number"
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
              setError("");
            }}
            onKeyPress={handleKeyPress}
            placeholder="Enter amount in INR"
            min="0"
            step="0.01"
            autoFocus
          />
          {error && <p className="error-message">{error}</p>}
        </div>

        <div className="price-preview">
          <p>
            Formatted price:{" "}
            {price ? `₹${parseFloat(price).toLocaleString("en-IN")}` : "₹0"}
          </p>
        </div>

        <div className="modal-actions">
          <button className="cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="modal-actions-confirm-btn" onClick={handleConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
});

// Alert Component
const AlertModal = React.memo(({ isOpen, message, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="packagedetails-modal-content alert-modal">
        <p>{message}</p>
        <div className="modal-actions">
          <button className="confirm-btn" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
});

// Image Preview Modal Component
const ImagePreviewModal = React.memo(({ isOpen, imageSrc, filename, onClose }) => {
  if (!isOpen || !imageSrc) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="packagedetails-modal-content image-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="image-preview-header">
          <h3>Image Preview</h3>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="image-preview-container">
          <img
            src={imageSrc}
            alt={filename || "Preview"}
            className="preview-image"
            loading="lazy"
          />
          {filename && <p className="preview-filename">{filename}</p>}
        </div>
      </div>
    </div>
  );
});

// Map Modal Component
const MapModal = React.memo(({
  isOpen,
  onClose,
  onLocationSelect,
  selectedCoords,
  mapSearch,
  onSearchChange,
  currentDay,
}) => {
  const [internalSearch, setInternalSearch] = useState("");
  const [mapKey, setMapKey] = useState(Date.now());

  useEffect(() => {
    if (isOpen) {
      setInternalSearch(mapSearch || "");
      setMapKey(Date.now());
    }
  }, [isOpen]);

  const handleSearchChange = useCallback((value) => {
    setInternalSearch(value);
    onSearchChange(value);
  }, [onSearchChange]);

  const handleLocationSelect = useCallback((coords) => {
    onLocationSelect(coords);
  }, [onLocationSelect]);

  const handleClose = useCallback(() => {
    setInternalSearch("");
    onClose();
  }, [onClose]);

  const normalizedCoords = normalizeCoordinates(selectedCoords);

  if (!isOpen) return null;

  return (
    <div className="package-map-modal-overlay">
      <div className="package-map-modal-content">
        <div className="package-map-modal-header">
          <h3>Select Location for Day {currentDay}</h3>
          <button className="package-close-map-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="package-map-search-container">
          <input
            type="text"
            placeholder="Search for a location..."
            className="package-map-search-input"
            value={internalSearch}
            onChange={(e) => handleSearchChange(capitalizeWords(e.target.value))}
          />
        </div>

        <div className="package-real-map-container">
          <GoogleMap
            key={mapKey}
            onLocationSelect={handleLocationSelect}
            selectedCoords={normalizedCoords}
            searchLocation={internalSearch}
            forceRefresh={isOpen}
          />
        </div>

        <div className="package-map-modal-actions">
          <button onClick={handleClose} className="package-confirm-location-btn">
            Confirm Location
          </button>
          <button onClick={handleClose} className="package-cancel-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
});

// Package Preview Component
const PackagePreviewModal = React.memo(({ isOpen, packageData, onClose }) => {
  if (!isOpen) return null;

  const { days, price, summary } = packageData;

  return (
    <div className="package-preview-overlay" onClick={onClose}>
      <div
        className="package-preview-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="package-preview-header">
          <h2>Package Preview</h2>
          <div className="package-summary-badge">
            {summary?.totalDays && (
              <span>
                {summary.totalDays} Days{" "}
                {summary.totalNights ? `/ ${summary.totalNights} Nights` : ""}
              </span>
            )}
          </div>
        </div>

        <div className="package-preview-content">
          <div className="package-preview-section">
            <h3>Package Overview</h3>
            <div className="preview-overview-grid">
              <div className="preview-overview-item">
                <strong>Total Days:</strong> {days.length}
              </div>
              <div className="preview-overview-item">
                <strong>Total Nights:</strong> {summary?.totalNights || 0}
              </div>
              <div className="preview-overview-item">
                <strong>Total Activities:</strong>{" "}
                {days.reduce(
                  (acc, day) => acc + (day.activities?.length || 0),
                  0
                )}
              </div>
              <div className="preview-overview-item">
                <strong>Price:</strong> {price?.formatted || "Not set"}
              </div>
            </div>
          </div>

          <div className="package-preview-section">
            <h3>Daily Itinerary</h3>
            {days.map((day, index) => (
              <div key={index} className="preview-day">
                <h4 className="preview-day-title">
                  Day {day.day_number || index + 1}
                  {day.night_number && (
                    <span className="preview-night-badge">
                      {" "}
                      • Night {day.night_number}
                    </span>
                  )}
                  {!day.has_overnight_stay && (
                    <span className="preview-no-stay-badge">
                      {" "}
                      • No Overnight Stay
                    </span>
                  )}
                </h4>

                <div className="preview-day-section">
                  <h5>Start Location & Timing</h5>
                  <div className="preview-day-details-grid">
                    <div className="preview-detail-item">
                      <strong>Location:</strong>{" "}
                      {day.start_location || "Not specified"}
                    </div>
                    {day.location_coords && (
                      <div className="preview-detail-item">
                        <strong>Coordinates:</strong>{" "}
                        {`Lat: ${
                          day.location_coords.latitude?.toFixed(6) ||
                          day.location_coords.lat?.toFixed(6)
                        }, Lng: ${
                          day.location_coords.longitude?.toFixed(6) ||
                          day.location_coords.lng?.toFixed(6)
                        }`}
                      </div>
                    )}
                    <div className="preview-detail-item">
                      <strong>Time:</strong>{" "}
                      {day.start_date_time
                        ? day.start_date_time
                        : "Not specified"}
                    </div>
                    <div className="preview-detail-item">
                      <strong>Pickup:</strong>{" "}
                      {day.pickup_mode === "available"
                        ? "Available"
                        : "Not Available"}
                    </div>
                    {day.pickup_mode === "available" && day.pickup_details && (
                      <div className="preview-detail-item">
                        <strong>Pickup Details:</strong> {day.pickup_details}
                      </div>
                    )}
                  </div>
                </div>

                <div className="preview-day-section">
                  <h5>Fooding Details</h5>
                  <div className="preview-day-details-grid">
                    <div className="preview-detail-item">
                      <strong>Food Type:</strong>{" "}
                      {day.fooding_option?.charAt(0).toUpperCase() +
                        day.fooding_option?.slice(1) || "Not specified"}
                    </div>
                    <div className="preview-detail-item">
                      <strong>Meals Included:</strong>{" "}
                      {day.selected_meals && day.selected_meals.length > 0
                        ? day.selected_meals
                            .map(
                              (meal) =>
                                meal.charAt(0).toUpperCase() + meal.slice(1)
                            )
                            .join(", ")
                        : "None"}
                    </div>
                  </div>
                </div>

                {day.day_description && (
                  <div className="preview-day-section">
                    <h5>Day Plan</h5>
                    <p className="preview-day-description">
                      {day.day_description}
                    </p>
                  </div>
                )}

                {day.activities && day.activities.length > 0 && (
                  <div className="preview-day-section">
                    <h5>Activities</h5>
                    <div className="preview-activities-list">
                      {day.activities.map((activity, activityIndex) => (
                        <div key={activityIndex} className="preview-activity">
                          <div className="preview-activity-header">
                            <strong>
                              {activity.activity_name ||
                                `Activity ${activityIndex + 1}`}
                            </strong>
                          </div>
                          {activity.activity_description && (
                            <p className="preview-activity-description">
                              {activity.activity_description}
                            </p>
                          )}
                          {(activity.activity_image || activity.activity_image_url) && (
                            <div className="preview-activity-image">
                              <img
                                src={activity.activity_image || activity.activity_image_url}
                                alt={
                                  activity.activity_name ||
                                  `Activity ${activityIndex + 1}`
                                }
                                className="preview-image-thumbnail"
                                loading="lazy"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(day.stay_type || day.property_name || day.property_address) &&
                  day.has_overnight_stay && (
                    <div className="preview-day-section">
                      <h5>Accommodation (Night {day.night_number})</h5>
                      <div className="preview-accommodation-details">
                        {day.stay_type && (
                          <div className="preview-detail-item">
                            <strong>Stay Type:</strong> {day.stay_type}
                          </div>
                        )}
                        {day.property_name && (
                          <div className="preview-detail-item">
                            <strong>Property Name:</strong> {day.property_name}
                          </div>
                        )}
                        {day.property_address && (
                          <div className="preview-detail-item">
                            <strong>Address:</strong> {day.property_address}
                          </div>
                        )}
                        {day.accommodation_images &&
                          day.accommodation_images.length > 0 && (
                            <div className="preview-accommodation-images">
                              <div className="preview-images-grid">
                                {day.accommodation_images.map((image, imgIndex) => (
                                  <div key={imgIndex} className="preview-image-item">
                                    <img
                                      src={image.image_data || image.image_url}
                                      alt={`Accommodation ${imgIndex + 1}`}
                                      className="preview-image-thumbnail"
                                      loading="lazy"
                                    />
                                    {image.filename && (
                                      <p className="preview-image-filename">
                                        {image.filename}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                {!day.has_overnight_stay && (
                  <div className="preview-day-section">
                    <h5>Accommodation</h5>
                    <p className="preview-no-accommodation">
                      No overnight stay for this day
                    </p>
                  </div>
                )}

                <hr className="preview-day-divider" />
              </div>
            ))}
          </div>
        </div>

        <div className="preview-modal-actions">
          <button className="preview-confirm-btn" onClick={onClose}>
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
});

const PackageDetails = () => {
  const [days, setDays] = useState([getEmptyDayTemplate()]);
  const [currentDay, setCurrentDay] = useState(1);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showPricePopup, setShowPricePopup] = useState(false);
  const [showAlert, setShowAlert] = useState({ open: false, message: "" });
  const [showImagePreview, setShowImagePreview] = useState({
    open: false,
    imageSrc: "",
    filename: "",
  });
  const [showPackagePreview, setShowPackagePreview] = useState(false);
  const [packagePrice, setPackagePrice] = useState(null);
  const [packageSummary, setPackageSummary] = useState({
    totalDays: 0,
    totalNights: 0,
  });

  const [showMap, setShowMap] = useState(false);
  const [selectedCoords, setSelectedCoords] = useState(null);
  const [mapSearch, setMapSearch] = useState("");
  const [currentMapDay, setCurrentMapDay] = useState(1);

  const navigate = useNavigate();
  const location = useLocation();
  const headerComponentsRef = useRef(null);
  const scrollWrapperRef = useRef(null);
  const mainContentRef = useRef(null);

  // FIX: Always scroll to top when component mounts or currentDay changes
  useEffect(() => {
    scrollToTop();
  }, [currentDay]);

  // Fix for blinking issue - OPTIMIZED
  useEffect(() => {
    const hasOpenModal =
      showResetConfirm ||
      showPricePopup ||
      showAlert.open ||
      showImagePreview.open ||
      showPackagePreview ||
      showMap;

    document.body.style.overflow = hasOpenModal ? "hidden" : "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [
    showResetConfirm,
    showPricePopup,
    showAlert.open,
    showImagePreview.open,
    showPackagePreview,
    showMap,
  ]);

  // Calculate day/night summary - OPTIMIZED
  useEffect(() => {
    const totalDays = days.length;
    const totalNights = days.filter(
      (day) =>
        !day.noAccommodation &&
        (day.stayType || day.propertyName || day.propertyAddress)
    ).length;

    setPackageSummary({ totalDays, totalNights });
  }, [days]);

  // Check if we have package data from CreatePackage - OPTIMIZED
  useEffect(() => {
    const checkPackageData = () => {
      const urlParams = new URLSearchParams(location.search);
      const packageId = urlParams.get("id");

      if (packageId) {
        localStorage.setItem("currentPackageId", packageId);
        setShowAlert({
          open: true,
          message: `Add Day details.`,
        });
      } else {
        const savedPackageId = localStorage.getItem("currentPackageId");
        if (!savedPackageId) {
          setShowAlert({
            open: true,
            message: "No package found. Please create a package first.",
          });
          setTimeout(() => {
            navigate("/create-package");
          }, 3000);
        }
      }
    };

    checkPackageData();
  }, [location, navigate]);

  // Handle map location selection - OPTIMIZED
  const handleMapLocationSelect = useCallback(async (coords) => {
    if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') {
      return;
    }

    setSelectedCoords(coords);

    try {
      const authToken = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.REVERSE_GEOCODE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ lat: coords.lat, lng: coords.lng }),
        }
      );

      const result = await response.json();

      if (result.success && result.results.length > 0) {
        const address = result.results[0].formatted_address;

        setDays(prevDays => {
          const updatedDays = [...prevDays];
          updatedDays[currentMapDay - 1] = {
            ...updatedDays[currentMapDay - 1],
            startLocation: capitalizeWords(address),
            locationCoords: {
              latitude: coords.lat,
              longitude: coords.lng,
              formatted_address: capitalizeWords(address),
            },
          };
          return updatedDays;
        });

        setShowAlert({
          open: true,
          message: `Location selected for Day ${currentMapDay}: ${capitalizeWords(address)}`,
        });

        setTimeout(() => {
          setShowAlert({ open: false, message: "" });
        }, 3000);
      } else {
        // Fallback: Use coordinates directly
        setDays(prevDays => {
          const updatedDays = [...prevDays];
          updatedDays[currentMapDay - 1] = {
            ...updatedDays[currentMapDay - 1],
            startLocation: `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`,
            locationCoords: {
              latitude: coords.lat,
              longitude: coords.lng,
              formatted_address: `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`,
            },
          };
          return updatedDays;
        });
      }
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
      // Fallback: Use coordinates directly
      setDays(prevDays => {
        const updatedDays = [...prevDays];
        updatedDays[currentMapDay - 1] = {
          ...updatedDays[currentMapDay - 1],
          startLocation: `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`,
          locationCoords: {
            latitude: coords.lat,
            longitude: coords.lng,
            formatted_address: `Lat: ${coords.lat.toFixed(6)}, Lng: ${coords.lng.toFixed(6)}`,
          },
        };
        return updatedDays;
      });
    }
  }, [currentMapDay]);

  // Open map for day - OPTIMIZED
  const openMapForDay = useCallback((dayNumber) => {
    setCurrentMapDay(dayNumber);
    
    const currentDayData = days[dayNumber - 1];
    const currentCoords = currentDayData?.locationCoords;
    
    const normalizedCoords = normalizeCoordinates(currentCoords);
    setSelectedCoords(normalizedCoords);
    
    setMapSearch(currentDayData?.startLocation || "");
    
    setTimeout(() => {
      setShowMap(true);
    }, 100);
  }, [days]);

  const closeMap = useCallback(() => {
    setShowMap(false);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, []);

  const updateHeaderAlignment = useCallback(() => {
    setTimeout(() => {
      if (!scrollWrapperRef.current || !headerComponentsRef.current) return;

      const cardCount = days.length;

      if (cardCount <= 3) {
        headerComponentsRef.current.classList.add("center-justify");
        headerComponentsRef.current.classList.remove("flex-start-justify");
        scrollWrapperRef.current.classList.add("center-justify");
        scrollWrapperRef.current.classList.remove("flex-start-justify");
      } else {
        headerComponentsRef.current.classList.add("flex-start-justify");
        headerComponentsRef.current.classList.remove("center-justify");
        scrollWrapperRef.current.classList.add("flex-start-justify");
        scrollWrapperRef.current.classList.remove("center-justify");
      }
    }, 100);
  }, [days.length]);

  useEffect(() => {
    updateHeaderAlignment();
  }, [updateHeaderAlignment]);

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setShowAlert({
        open: true,
        message: "Please login first",
      });
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }
    initializePackageDetails();
  }, [navigate]);

  const initializePackageDetails = async () => {
    try {
      const savedData = await loadFromIndexedDB();
      if (savedData) {
        setDays(Array.isArray(savedData) ? savedData : [savedData]);
      } else {
        const saved = localStorage.getItem("dayData");
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            const dataToSet = Array.isArray(parsed) ? parsed : [parsed];
            setDays(dataToSet);
            await saveToIndexedDB(dataToSet);
            localStorage.removeItem("dayData");
          } catch (e) {
            setDays([getEmptyDayTemplate()]);
          }
        }
      }
    } catch (error) {
      const saved = localStorage.getItem("dayData");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setDays(Array.isArray(parsed) ? parsed : [parsed]);
        } catch (e) {
          setDays([getEmptyDayTemplate()]);
        }
      }
    }
  };

  // Image upload handling
  const handleImageUpload = useCallback(async (file, maxSize = VALIDATION.MAX_FILE_SIZE) => {
    return new Promise((resolve, reject) => {
      if (!VALIDATION.VALID_IMAGE_TYPES.includes(file.type)) {
        reject(new Error(`Invalid file type: ${file.type}. Please select JPEG, PNG, GIF, or WebP image.`));
        return;
      }

      if (file.size > maxSize) {
        const fileSizeFormatted = (file.size / (1024 * 1024)).toFixed(2) + "MB";
        const maxSizeFormatted = (maxSize / (1024 * 1024)).toFixed(2) + "MB";
        reject(new Error(`File too large: ${fileSizeFormatted}. Maximum allowed size is ${maxSizeFormatted}.`));
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) =>
        resolve({
          base64: ev.target.result,
          filename: file.name,
          size: file.size,
          type: file.type,
          file: file
        });
      reader.onerror = () => reject(new Error("Error reading file. Please try again."));
      reader.readAsDataURL(file);
    });
  }, []);

  // Upload image to S3
  const uploadImageToS3 = useCallback(async (file, type, dayIndex, activityIndex = null) => {
    try {
      const authToken = localStorage.getItem("authToken");
      
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', type);
      formData.append('dayIndex', dayIndex);
      
      if (type === 'activity') {
        formData.append('activityIndex', activityIndex);
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_IMAGE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to upload image to S3');
      }
    } catch (error) {
      throw error;
    }
  }, []);

  // Handle activity image upload
  const handleActivityImageUpload = useCallback(async (dayIndex, activityIndex, file) => {
    try {
      setIsProcessingUpload(true);
      
      const imageData = await handleImageUpload(file);
      const s3Data = await uploadImageToS3(file, 'activity', dayIndex, activityIndex);

      setDays(prevDays => {
        const updatedDays = [...prevDays];
        if (!updatedDays[dayIndex].activities) {
          updatedDays[dayIndex].activities = [];
        }
        if (!updatedDays[dayIndex].activities[activityIndex]) {
          updatedDays[dayIndex].activities[activityIndex] = {};
        }

        updatedDays[dayIndex].activities[activityIndex] = {
          ...updatedDays[dayIndex].activities[activityIndex],
          activity_image: imageData.base64,
          activity_image_url: s3Data.url,
          activity_image_key: s3Data.key,
          activity_filename: s3Data.filename,
        };

        return updatedDays;
      });

      setShowAlert({
        open: true,
        message: "Activity image uploaded successfully!",
      });
    } catch (error) {
      setShowAlert({
        open: true,
        message: error.message,
      });
    } finally {
      setIsProcessingUpload(false);
    }
  }, [handleImageUpload, uploadImageToS3]);

  // Handle accommodation image upload
  const handleAccommodationImageUpload = useCallback(async (dayIndex, file) => {
    try {
      setIsProcessingUpload(true);
      
      const imageData = await handleImageUpload(file);
      const s3Data = await uploadImageToS3(file, 'accommodation', dayIndex);

      setDays(prevDays => {
        const updatedDays = [...prevDays];
        if (!updatedDays[dayIndex].accommodationImages) {
          updatedDays[dayIndex].accommodationImages = [];
        }

        updatedDays[dayIndex].accommodationImages = [
          {
            image: imageData.base64,
            image_url: s3Data.url,
            image_key: s3Data.key,
            filename: s3Data.filename,
          },
        ];

        return updatedDays;
      });

      setShowAlert({
        open: true,
        message: "Accommodation image uploaded successfully!",
      });
    } catch (error) {
      setShowAlert({
        open: true,
        message: error.message,
      });
    } finally {
      setIsProcessingUpload(false);
    }
  }, [handleImageUpload, uploadImageToS3]);

  const removeActivityImage = useCallback((dayIndex, activityIndex) => {
    setDays(prevDays => {
      const updatedDays = [...prevDays];
      if (updatedDays[dayIndex]?.activities?.[activityIndex]) {
        updatedDays[dayIndex].activities[activityIndex] = {
          ...updatedDays[dayIndex].activities[activityIndex],
          activity_image: "",
          activity_image_url: "",
          activity_image_key: "",
          activity_filename: "",
        };
      }
      return updatedDays;
    });
  }, []);

  const removeAccommodationImage = useCallback((dayIndex) => {
    setDays(prevDays => {
      const updatedDays = [...prevDays];
      if (updatedDays[dayIndex]) {
        updatedDays[dayIndex].accommodationImages = [];
      }
      return updatedDays;
    });
  }, []);

  // File input handler
  const handleFileInput = useCallback((event, type, dayIndex, activityIndex = null) => {
    const file = event.target.files[0];
    if (!file) return;

    if (type === "activity") {
      handleActivityImageUpload(dayIndex, activityIndex, file);
    } else if (type === "accommodation") {
      handleAccommodationImageUpload(dayIndex, file);
    }

    event.target.value = "";
  }, [handleActivityImageUpload, handleAccommodationImageUpload]);

  const showImagePreviewModal = useCallback((imageSrc, filename) => {
    setShowImagePreview({
      open: true,
      imageSrc,
      filename,
    });
  }, []);

  const closeImagePreviewModal = useCallback(() => {
    setShowImagePreview({
      open: false,
      imageSrc: "",
      filename: "",
    });
  }, []);

  const closePackagePreview = useCallback(() => {
    setShowPackagePreview(false);
  }, []);

  const addDay = useCallback(() => {
    const newDay = getEmptyDayTemplate();
    setDays(prevDays => [...prevDays, newDay]);
    setCurrentDay(prev => prev + 1);

    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [scrollToTop]);

  const deleteDay = useCallback((dayNumber) => {
    if (days.length <= 1) {
      setShowAlert({
        open: true,
        message: "Cannot delete the only day",
      });
      return;
    }

    setDays(prevDays => prevDays.filter((_, index) => index !== dayNumber - 1));
    setCurrentDay(prev => Math.min(prev, days.length - 1));
  }, [days.length]);

  const selectDay = useCallback((dayNumber) => {
    setCurrentDay(dayNumber);
    setTimeout(() => {
      scrollToTop();
    }, 100);
  }, [scrollToTop]);

  const saveCurrentDay = useCallback(async () => {
    if (currentDay < 1 || currentDay > days.length) return;

    setDays(prevDays => {
      const updatedDays = [...prevDays];
      updatedDays[currentDay - 1] = {
        ...updatedDays[currentDay - 1],
        isSaved: true,
      };
      return updatedDays;
    });

    try {
      const currentDays = days.map((day, index) => 
        index === currentDay - 1 ? {...day, isSaved: true} : day
      );
      await saveToIndexedDB(currentDays);

      setShowAlert({
        open: true,
        message: `Day ${currentDay} saved successfully!`,
      });
    } catch (error) {
      try {
        const currentDays = days.map((day, index) => 
          index === currentDay - 1 ? {...day, isSaved: true} : day
        );
        localStorage.setItem("dayData", JSON.stringify(currentDays));
        setShowAlert({
          open: true,
          message: `Day ${currentDay} saved (using fallback storage)!`,
        });
      } catch (localStorageError) {
        setShowAlert({
          open: true,
          message: "Error saving data. Please try again.",
        });
      }
    }
  }, [currentDay, days]);

  // Collect all days data
  const collectAllDaysData = useCallback(() => {
    return days.map((day, index) => {
      return {
        day_number: index + 1,
        night_number: day.nightNumber,
        has_overnight_stay: !day.noAccommodation,
        start_location: day.startLocation || "",
        start_date_time: day.startTime ? `${day.startTime} ${day.startAmPm}` : "",
        pickup_mode: day.pickupMode || PICKUP_MODES[0],
        pickup_details: day.pickupDetails || "",
        fooding_option: day.foodingOption || FOODING_OPTIONS[0],
        selected_meals: day.selectedMeals || [],
        day_description: day.dayDescription || "",
        activities: (day.activities || []).map((activity) => ({
          activity_name: activity.name || "",
          activity_description: activity.description || "",
          activity_image: activity.activity_image || "",
          activity_image_url: activity.activity_image_url || "",
          activity_image_key: activity.activity_image_key || "",
          activity_filename: activity.activity_filename || "",
        })),
        stay_type: day.stayType || "",
        property_name: day.propertyName || "",
        property_address: day.propertyAddress || "",
        accommodation_images: (day.accommodationImages || []).map((img) => ({
          image_data: img.image || "",
          image_url: img.image_url || "",
          image_key: img.image_key || "",
          filename: img.filename || "",
        })),
        location_coords: day.locationCoords || null,
        no_accommodation: day.noAccommodation || false,
      };
    });
  }, [days]);

  // Update day field
  const updateDayField = useCallback((field, value) => {
    setDays(prevDays => {
      const updatedDays = [...prevDays];

      let formattedValue = value;

      if (field === "startLocation") {
        formattedValue = capitalizeWords(value);
      } else if (field === "pickupDetails" || field === "dayDescription" || 
                 field === "stayType" || field === "propertyName" || 
                 field === "propertyAddress") {
        formattedValue = capitalizeFirstLetter(value);
      }

      if (field === "noAccommodation") {
        updatedDays[currentDay - 1] = {
          ...updatedDays[currentDay - 1],
          [field]: value,
          ...(value && {
            stayType: "",
            propertyName: "",
            propertyAddress: "",
            accommodationImages: [],
          }),
        };
      } else if (field === "pickupMode") {
        const sanitizedValue = PICKUP_MODES.includes(value) ? value : PICKUP_MODES[0];
        
        updatedDays[currentDay - 1] = {
          ...updatedDays[currentDay - 1],
          [field]: sanitizedValue,
          pickupDetails:
            sanitizedValue === "not-available"
              ? ""
              : updatedDays[currentDay - 1].pickupDetails,
        };
      } else {
        updatedDays[currentDay - 1] = {
          ...updatedDays[currentDay - 1],
          [field]: formattedValue,
        };
      }

      return updatedDays;
    });
  }, [currentDay]);

  // Update activity field
  const updateActivityField = useCallback((activityIndex, field, value) => {
    setDays(prevDays => {
      const updatedDays = [...prevDays];
      if (!updatedDays[currentDay - 1].activities) {
        updatedDays[currentDay - 1].activities = [];
      }
      if (!updatedDays[currentDay - 1].activities[activityIndex]) {
        updatedDays[currentDay - 1].activities[activityIndex] = {};
      }

      let formattedValue = value;
      
      if (field === "name" || field === "description") {
        formattedValue = capitalizeFirstLetter(value);
      }

      updatedDays[currentDay - 1].activities[activityIndex] = {
        ...updatedDays[currentDay - 1].activities[activityIndex],
        [field]: formattedValue,
      };

      return updatedDays;
    });
  }, [currentDay]);

  const handleResetClick = useCallback(() => {
    setShowResetConfirm(true);
  }, []);

  const confirmReset = useCallback(async () => {
    try {
      await clearIndexedDB();
    } catch (error) {
      console.error("Error clearing IndexedDB:", error);
    }

    localStorage.removeItem("dayData");

    setDays([getEmptyDayTemplate()]);
    setCurrentDay(1);
    setShowResetConfirm(false);
  }, []);

  const cancelReset = useCallback(() => {
    setShowResetConfirm(false);
  }, []);

  // Create package
  const createPackage = useCallback(async (dayData, priceData) => {
    try {
      const packageId = localStorage.getItem("currentPackageId");

      if (!packageId) {
        throw new Error("Package ID not found. Please create a package first.");
      }

      if (!dayData || dayData.length === 0) {
        throw new Error("No day data provided");
      }

      if (!priceData || !priceData.raw || priceData.raw <= 0) {
        throw new Error("Invalid price data provided");
      }

      const apiData = {
        days: dayData,
        price: priceData.raw,
        totalDays: dayData.length,
      };

      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.PACKAGE_DETAILS}/${packageId}/details`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(apiData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        const summaryMessage = result.summary
          ? `Package created successfully with ${result.summary.totalDays} days and ${result.summary.totalNights} nights!\nPrice: ${priceData.formatted}`
          : `Package created successfully with ${dayData.length} days!\nPrice: ${priceData.formatted}`;

        setShowAlert({
          open: true,
          message: summaryMessage,
        });

        try {
          await clearIndexedDB();
        } catch (error) {
          console.error("Error clearing IndexedDB:", error);
        }
        localStorage.removeItem("packageData");
        localStorage.removeItem("dayData");
        localStorage.removeItem("currentPackageId");

        setDays([getEmptyDayTemplate()]);

        setTimeout(() => {
          navigate("/home");
        }, 3000);
      } else {
        throw new Error(result.message || "Failed to create package");
      }
    } catch (error) {
      setShowAlert({
        open: true,
        message: `Error creating package: ${error.message}`,
      });
    }
  }, [navigate]);

  const handlePreview = useCallback(() => {
    const previewData = collectAllDaysData();
    const hasValidData = previewData.some(
      (day) =>
        day.start_location ||
        day.day_description ||
        (day.activities &&
          day.activities.some(
            (activity) =>
              activity.activity_name || activity.activity_description
          )) ||
        day.stay_type ||
        day.property_name ||
        day.property_address
    );

    if (!hasValidData) {
      setShowAlert({
        open: true,
        message: "Please add some data to your package before previewing.",
      });
      return;
    }

    setPackagePrice({ formatted: "Not set" });
    setShowPackagePreview(true);
  }, [collectAllDaysData]);

  const handleConfirm = useCallback(() => {
    const finalDayData = collectAllDaysData();

    const hasValidData = finalDayData.some(
      (day) =>
        day.start_location ||
        day.day_description ||
        (day.activities &&
          day.activities.some(
            (activity) =>
              activity.activity_name || activity.activity_description
          )) ||
        day.stay_type ||
        day.property_name ||
        day.property_address
    );

    if (!hasValidData) {
      setShowAlert({
        open: true,
        message: "Please add some package details before confirming.",
      });
      return;
    }

    setShowPricePopup(true);
  }, [collectAllDaysData]);

  const handlePriceConfirm = useCallback((priceData) => {
    if (priceData) {
      setPackagePrice(priceData);
      createPackage(collectAllDaysData(), priceData);
    }
    setShowPricePopup(false);
  }, [collectAllDaysData, createPackage]);

  const handlePriceCancel = useCallback(() => {
    setShowPricePopup(false);
  }, []);

  // FIXED: Add only one activity at a time with proper state update
  const addActivity = useCallback((dayIndex) => {
    setDays(prevDays => {
      const updatedDays = [...prevDays];
      
      // Ensure activities array exists
      if (!updatedDays[dayIndex].activities) {
        updatedDays[dayIndex].activities = [];
      }
      
      // Add only ONE empty activity using functional update
      const newActivity = {
        name: "",
        description: "",
        activity_image: "",
        activity_image_url: "",
        activity_image_key: "",
        activity_filename: "",
      };
      
      return updatedDays.map((day, index) => {
        if (index === dayIndex) {
          return {
            ...day,
            activities: [...(day.activities || []), newActivity]
          };
        }
        return day;
      });
    });
  }, []);

  const removeActivity = useCallback((dayIndex, activityIndex) => {
    setDays(prevDays => {
      const updatedDays = [...prevDays];
      if (updatedDays[dayIndex].activities) {
        updatedDays[dayIndex].activities.splice(activityIndex, 1);
      }
      return updatedDays;
    });
  }, []);

  const handleAlertClose = useCallback(() => {
    if (showAlert.message.includes("Please login first")) {
      navigate("/login");
    }
    setShowAlert({ open: false, message: "" });
  }, [showAlert.message, navigate]);

  const renderHeaderCards = useCallback(() => {
    return days.map((day, index) => (
      <div
        key={index + 1}
        className={`card ${currentDay === index + 1 ? "selected-card" : ""} ${
          day.isSaved ? "saved-card" : ""
        }`}
        data-day={index + 1}
        onClick={() => selectDay(index + 1)}
      >
        <h3 className="card-titles">Day {index + 1}</h3>
        <button
          className="delete-card-btn"
          onClick={(e) => {
            e.stopPropagation();
            deleteDay(index + 1);
          }}
        >
          X
        </button>
        {day.isSaved && <div className="saved-badge">✓ Saved</div>}
        <div className="card-contents-parent">
          {[
            { label: "Start Point", iconImage: startDayIcon },
            { label: "Day Plan", iconImage: dayPlanIcon },
            { label: "Accommodation", iconImage: accommodationIcon },
          ].map((content, idx) => (
            <div key={idx} className="card-contents">
              <div className="card-icons">
                <img src={content.iconImage} alt={content.label} loading="lazy" />
              </div>
              <div className="card-icon-label">{content.label}</div>
            </div>
          ))}
        </div>
        {!day.noAccommodation &&
          (day.stayType || day.propertyName || day.propertyAddress) && (
            <div className="night-badge">Night {index + 1}</div>
          )}
        {day.noAccommodation && <div className="no-stay-badge">No Stay</div>}
      </div>
    ));
  }, [days, currentDay, selectDay, deleteDay]);

  const renderDayDetails = useCallback(() => {
    const currentDayData = days[currentDay - 1] || {};

    return (
      <div className="main-body" ref={mainContentRef}>
        <div className="main-content">
          <div className="day-header">
            <h2>Day {currentDay} Details</h2>
            <div className="day-night-indicator">
              {!currentDayData.noAccommodation &&
              (currentDayData.stayType ||
                currentDayData.propertyName ||
                currentDayData.propertyAddress) ? (
                <span className="night-indicator">• Night {currentDay}</span>
              ) : currentDayData.noAccommodation ? (
                <span className="no-stay-indicator">• No Overnight Stay</span>
              ) : null}
            </div>
          </div>

          <div className="location-container">
            <div className="location-div1">
              <div className="location-container-input-loc">
                <label className="location-container-input-loc-label">
                  Enter Location
                </label>
              </div>

              <div className="location-container-input-sec">
                <button
                  type="button"
                  className="package-select-location-btn"
                  onClick={() => openMapForDay(currentDay)}
                >
                  🗺️ Select on Map
                </button>
                <input
                  className="location-container-input"
                  placeholder="Select from the map"
                  value={currentDayData.startLocation || ""}
                  onChange={(e) =>
                    updateDayField("startLocation", e.target.value)
                  }
                  readOnly
                />
              </div>

              {currentDayData.locationCoords && (
                <div className="package-selected-location-info">
                  <small>
                    Coordinates:{" "}
                    {`Lat: ${
                      currentDayData.locationCoords.latitude?.toFixed(6) ||
                      currentDayData.locationCoords.lat?.toFixed(6)
                    }, Lng: ${
                      currentDayData.locationCoords.longitude?.toFixed(6) ||
                      currentDayData.locationCoords.lng?.toFixed(6)
                    }`}
                  </small>
                </div>
              )}

              <div className="location-container-div2">
                <div className="location-container-div2-a">
                  <div className="location-container-time">
                    <label className="location-container-time-lab">
                      Select Time
                    </label>
                    <div className="time-input-wrapper">
                      <input
                        type="time"
                        className="location-container-time-input"
                        value={currentDayData.startTime || ""}
                        onChange={(e) =>
                          updateDayField("startTime", e.target.value)
                        }
                      />
                      <select
                        value={currentDayData.startAmPm || TIME_OPTIONS[0]}
                        onChange={(e) =>
                          updateDayField("startAmPm", e.target.value)
                        }
                      >
                        {TIME_OPTIONS.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="location-container-pickup">
                    <label className="location-container-pickup-lab">
                      Pick up
                    </label>
                    <div className="pickup-modes">
                      {PICKUP_MODES.map(mode => (
                        <label key={mode}>
                          <input
                            type="radio"
                            name={`pickupMode-${currentDay}`}
                            value={mode}
                            checked={currentDayData.pickupMode === mode}
                            onChange={(e) =>
                              updateDayField("pickupMode", e.target.value)
                            }
                          />
                          {mode === "available" ? "Available" : "Not Available"}
                        </label>
                      ))}
                    </div>

                    {currentDayData.pickupMode === "available" && (
                      <div className="pickup-details-section">
                        <label className="pickup-details-label">
                          Pickup Location Details
                        </label>
                        <input
                          type="text"
                          className="pickup-details-input"
                          placeholder="E.g. Airport, Train Station, Hotels.."
                          value={currentDayData.pickupDetails || ""}
                          onChange={(e) =>
                            updateDayField("pickupDetails", e.target.value)
                          }
                        />
                        <small className="pickup-details-hint">
                          Specify where exactly the pickup will be available
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                <div className="location-container-div2-b">
                  <div className="location-container-catering-manner">
                    <label className="location-container-catering-lab">
                      Catering manner
                    </label>
                    <div className="fooding-options">
                      {FOODING_OPTIONS.map((option) => (
                        <label key={option}>
                          <input
                            type="radio"
                            name={`foodingOption-${currentDay}`}
                            value={option}
                            checked={currentDayData.foodingOption === option}
                            onChange={(e) =>
                              updateDayField("foodingOption", e.target.value)
                            }
                          />
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="location-container-catering-meals">
                    <label className="location-container-catering-lab">
                      Catering
                    </label>
                    <div className="meals-checkboxes">
                      {MEAL_OPTIONS.map((meal) => (
                        <label key={meal}>
                          <input
                            type="checkbox"
                            checked={(
                              currentDayData.selectedMeals || []
                            ).includes(meal)}
                            onChange={(e) => {
                              const currentMeals =
                                currentDayData.selectedMeals || [];
                              let newMeals;

                              if (e.target.checked) {
                                newMeals = [...currentMeals, meal];
                              } else {
                                newMeals = currentMeals.filter(
                                  (m) => m !== meal
                                );
                              }

                              updateDayField("selectedMeals", newMeals);
                            }}
                          />
                          {meal.charAt(0).toUpperCase() + meal.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Day Plan Section */}
            <div className="plan">
              <div className="plan-cap">
                <h3>Day Plan</h3>
              </div>
              <div className="plan-section">
                <div className="plan-day-descp">
                  <label>Description</label>
                  <textarea
                    placeholder="Describe about the day"
                    value={currentDayData.dayDescription || ""}
                    onChange={(e) =>
                      updateDayField("dayDescription", e.target.value)
                    }
                  />
                </div>

                <div className="plan-act">
                  <label>Activities</label>
                  <div id="activities-container">
                    {/* FIXED: Use currentDayData.activities directly and ensure it's an array */}
                    {(currentDayData.activities || []).map(
                      (activity, index) => (
                        <div key={index} className="activity-row">
                          <div className="activity-holder-con">
                            <div className="plan-act-input-section">
                              <p>Set Activity Name</p>
                              <input
                                className="activityName"
                                placeholder="E.g: Sight Seeing, Trekking...."
                                value={activity.name || ""}
                                onChange={(e) =>
                                  updateActivityField(
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                              <p>Highlight of the Activity</p>
                              <input
                                className="activityName"
                                placeholder="E.g: Trying Zutho, Climbing Mt.Japfü,....."
                                value={activity.description || ""}
                                onChange={(e) =>
                                  updateActivityField(
                                    index,
                                    "description",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="plan-upload">
                              {activity.activity_image ? (
                                <div className="uploaded-file">
                                  <div
                                    className="image-preview-thumbnail"
                                    onClick={() =>
                                      showImagePreviewModal(
                                        activity.activity_image,
                                        activity.activity_filename
                                      )
                                    }
                                  >
                                    <img
                                      src={activity.activity_image}
                                      alt="Activity"
                                      className="thumbnail-image"
                                      loading="lazy"
                                    />
                                    <div className="preview-overlay">
                                      <span>Click to Preview</span>
                                    </div>
                                  </div>
                                  <span className="filename">
                                    {activity.activity_filename || "Uploaded Image"}
                                  </span>
                                  <div className="upload-actions">
                                    <button
                                      className="change"
                                      onClick={() =>
                                        document
                                          .getElementById(
                                            `activity-upload-${
                                              currentDay - 1
                                            }-${index}`
                                          )
                                          .click()
                                      }
                                    >
                                      Change
                                    </button>
                                    <button
                                      className="remove"
                                      onClick={() =>
                                        removeActivityImage(
                                          currentDay - 1,
                                          index
                                        )
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <input
                                    type="file"
                                    id={`activity-upload-${
                                      currentDay - 1
                                    }-${index}`}
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={(e) =>
                                      handleFileInput(
                                        e,
                                        "activity",
                                        currentDay - 1,
                                        index
                                      )
                                    }
                                  />
                                </div>
                              ) : (
                                <>
                                  <button
                                    title="Upload Image"
                                    onClick={() =>
                                      document
                                        .getElementById(
                                          `activity-upload-${
                                            currentDay - 1
                                          }-${index}`
                                        )
                                        .click()
                                    }
                                    disabled={isProcessingUpload}
                                  >
                                    <img src={upload} alt="Upload" loading="lazy" />
                                  </button>
                                  <input
                                    type="file"
                                    id={`activity-upload-${
                                      currentDay - 1
                                    }-${index}`}
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={(e) =>
                                      handleFileInput(
                                        e,
                                        "activity",
                                        currentDay - 1,
                                        index
                                      )
                                    }
                                  />
                                </>
                              )}
                              <p>
                                * Note that you can only upload 1 image per
                                activity.
                              </p>
                            </div>
                          </div>

                          <div className="remove-activity">
                            <button
                              style={{ borderRadius: "10px" }}
                              className="remove-activity"
                              onClick={() =>
                                removeActivity(currentDay - 1, index)
                              }
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                  <button
                    id="add-activity"
                    onClick={() => addActivity(currentDay - 1)}
                  >
                    Add Another Activity
                  </button>
                </div>
              </div>
            </div>

            {/* Accommodation Section */}
            <div className="accomodate">
              <div className="accomodate-cap">
                <h3>Accommodations</h3>
                <div className="accommodation-summary">
                  {packageSummary.totalNights > 0 && (
                    <span className="nights-count">
                      {packageSummary.totalNights} night(s) total
                    </span>
                  )}
                </div>
              </div>
              <div className="accomodate-input">
                <div className="no-accommodation-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      id="noAccommodation"
                      checked={currentDayData.noAccommodation || false}
                      onChange={(e) =>
                        updateDayField("noAccommodation", e.target.checked)
                      }
                    />
                    No Accommodation (No night stay for this day)
                  </label>
                </div>

                {!currentDayData.noAccommodation && (
                  <>
                    <label>Enter type of stay</label>
                    <div className="accomodate-input-sec">
                      <input
                        placeholder="E.g. Hotel, Homestay, PG..."
                        value={currentDayData.stayType || ""}
                        onChange={(e) =>
                          updateDayField("stayType", e.target.value)
                        }
                      />
                    </div>

                    <label>Enter Name of property</label>
                    <div className="accomodate-input-sec">
                      <input
                        placeholder="Enter property name"
                        value={currentDayData.propertyName || ""}
                        onChange={(e) =>
                          updateDayField("propertyName", e.target.value)
                        }
                      />
                    </div>

                    <label>Enter Location/Address</label>
                    <div className="accomodate-input-sec">
                      <input
                        placeholder="Enter address"
                        value={currentDayData.propertyAddress || ""}
                        onChange={(e) =>
                          updateDayField("propertyAddress", e.target.value)
                        }
                      />
                    </div>
                  </>
                )}

                {currentDayData.noAccommodation && (
                  <div className="no-accommodation-message">
                    <p>
                      ✅ No accommodation needed for Day {currentDay}. Travelers
                      will arrange their own stay or continue traveling.
                    </p>
                  </div>
                )}
              </div>

              {/* Upload Section - Only show if there's accommodation */}
              {!currentDayData.noAccommodation && (
                <div className="upload-section">
                  {currentDayData.accommodationImages &&
                  currentDayData.accommodationImages.length > 0 ? (
                    <div className="uploaded-file">
                      <div
                        className="image-preview-thumbnail"
                        onClick={() =>
                          showImagePreviewModal(
                            currentDayData.accommodationImages[0].image,
                            currentDayData.accommodationImages[0].filename
                          )
                        }
                      >
                        <img
                          src={currentDayData.accommodationImages[0].image}
                          alt="Accommodation"
                          className="thumbnail-image"
                          loading="lazy"
                        />
                        <div className="preview-overlay">
                          <span>Click to Preview</span>
                        </div>
                      </div>
                      <span className="filename">
                        {currentDayData.accommodationImages[0].filename ||
                          "Uploaded Image"}
                      </span>
                      <div className="upload-actions">
                        <button
                          className="change"
                          onClick={() =>
                            document
                              .getElementById(
                                `accommodation-upload-${currentDay - 1}`
                              )
                              .click()
                          }
                        >
                          Change
                        </button>
                        <button
                          className="remove"
                          onClick={() =>
                            removeAccommodationImage(currentDay - 1)
                          }
                        >
                          Remove
                        </button>
                      </div>
                      <p>*Image should be below 5 MB</p>
                      <input
                        type="file"
                        id={`accommodation-upload-${currentDay - 1}`}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) =>
                          handleFileInput(e, "accommodation", currentDay - 1)
                        }
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        title="Upload Image"
                        onClick={() =>
                          document
                            .getElementById(
                              `accommodation-upload-${currentDay - 1}`
                            )
                            .click()
                        }
                        disabled={isProcessingUpload}
                      >
                        <img src={upload} alt="Upload" loading="lazy" />
                      </button>
                      <p>*Image should be below 3 MB</p>
                      <input
                        type="file"
                        id={`accommodation-upload-${currentDay - 1}`}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={(e) =>
                          handleFileInput(e, "accommodation", currentDay - 1)
                        }
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="save-con">
              <small style={{color:"red",textDecoration:"none"}}>* Do not forget to save day details before adding new day or saving the package.</small>
              <button className="save-day-btn" onClick={saveCurrentDay}>
                Save Day {currentDay}
              </button>

              <button className="add-day-bottom" onClick={addDay}>
                + Add Day
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }, [days, currentDay, packageSummary, isProcessingUpload, openMapForDay, updateDayField, updateActivityField, addActivity, removeActivity, saveCurrentDay, addDay, showImagePreviewModal, handleFileInput, removeActivityImage, removeAccommodationImage]);

  return (
    <div className="package-details">
      <div className="header slide-in-top">
        <div className="header-top">
          <button className="back-arrow" onClick={() => navigate("/home")}>
            ←
          </button>
          <label className="header-title">Package Details</label>
        </div>

        <div className="package-summary-header">
          <span className="days-count">{packageSummary.totalDays} Days</span>
          {packageSummary.totalNights > 0 && (
            <span className="nights-count">
              {packageSummary.totalNights} Nights
            </span>
          )}
        </div>

        <div className="parent-header-wrapper">
          <div
            className={`header-scroll-wrapper ${
              days.length <= 3 ? "center-justify" : "flex-start-justify"
            }`}
            ref={scrollWrapperRef}
          >
            <div
              className={`header-components ${
                days.length <= 3 ? "center-justify" : "flex-start-justify"
              }`}
              ref={headerComponentsRef}
            >
              {renderHeaderCards()}
            </div>
          </div>
          <div className="add-card-wrapper">
            <button id="addCardBtn" className="add-card" onClick={addDay}>
              + Add Day
            </button>
            <button
              id="resetBtn"
              className="reset-btn"
              onClick={handleResetClick}
            >
              Full reset
            </button>
          </div>
        </div>
      </div>

      <div id="bodyContainer" className="bodyContainer">
        {renderDayDetails()}
      </div>

      <div className="preview slide-up">
        <button
          id="previewBtn"
          title="Preview Package"
          className="preview-btn"
          onClick={handlePreview}
        >
          Preview Package
        </button>
        <button
          id="confirmBtn"
          title="Confirm and Create Package"
          className="confirm-btn"
          onClick={handleConfirm}
        >
          Enter Price
        </button>
      </div>

      <ConfirmationModal
        isOpen={showResetConfirm}
        title="Reset Confirmation"
        message="Are you sure you want to reset all days? This will delete all your progress."
        onConfirm={confirmReset}
        onCancel={cancelReset}
        confirmText="Yes, Reset"
        cancelText="Cancel"
      />

      <PricePopup
        isOpen={showPricePopup}
        onConfirm={handlePriceConfirm}
        onCancel={handlePriceCancel}
      />

      <AlertModal
        isOpen={showAlert.open}
        message={showAlert.message}
        onClose={handleAlertClose}
      />

      <ImagePreviewModal
        isOpen={showImagePreview.open}
        imageSrc={showImagePreview.imageSrc}
        filename={showImagePreview.filename}
        onClose={closeImagePreviewModal}
      />

      <PackagePreviewModal
        isOpen={showPackagePreview}
        packageData={{
          days: collectAllDaysData(),
          price: packagePrice,
          summary: packageSummary,
        }}
        onClose={closePackagePreview}
      />

      <MapModal
        isOpen={showMap}
        onClose={closeMap}
        onLocationSelect={handleMapLocationSelect}
        selectedCoords={selectedCoords}
        mapSearch={mapSearch}
        onSearchChange={setMapSearch}
        currentDay={currentMapDay}
      />
    </div>
  );
};

export default React.memo(PackageDetails);